/**
 * jXRL response-shape helpers — port of legacy defensive-read patterns from
 *   spfx-account-opening-v2/.../services/CustSearchHelper.ts (lines 61-65) and
 *   spfx-account-opening-v2/.../services/JxChangeService.ts (txt + Array.isArray
 *   guards at lines 206-210, 213-222, 226-232).
 *
 * jXRL exposes three persistent response quirks that every reader must handle:
 *
 *   1. Null wrapper         — `{"#text": null}` means "no result", NOT a row.
 *   2. Rec | RecArray shape — `FooSrchRecArray` may be:
 *                               (a) a true JSON array of records,
 *                               (b) an object `{ FooSrchRec: [...] }` holding the
 *                                   array one level deeper, or
 *                               (c) a single record object (no array wrapper at all).
 *   3. Masked TaxId objects — `{"#text": "123456789", "@MaskVal": "XXX-XX-6789"}`
 *                              must be unwrapped to the raw string.
 *
 * These helpers are purposefully untyped at the argument edge (unknown) so
 * callers can pass raw jxrlPost responses without contortions, and they
 * return strongly typed output so downstream `.map` / `.filter` calls are safe.
 *
 * Audit ref: G-CC-05 (01-cross-cutting-gaps.md).
 */

/**
 * Internal: narrow a value to a plain object (non-array, non-null).
 */
function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

/**
 * Internal: detect the jXRL "#text":null null-wrapper, which is the server's
 * way of encoding "the XML element was present but empty". Treat as no data.
 */
function isNullWrapper(val: unknown): boolean {
  return isPlainObject(val) && '#text' in val && val['#text'] === null;
}

/**
 * Unwrap a jXRL array-shape response into a normalized `T[]`.
 *
 * Handles, in order:
 *   1) `resp[arrayKey]` is a JSON array                   → return it
 *   2) `resp[arrayKey]` is an object `{ [singular]: [...] }` → return inner array
 *   3) `resp[arrayKey]` is a single record object         → wrap and return
 *   4) `resp[arrayKey]` is `{"#text": null}` null-wrapper → return []
 *   5) `resp[arrayKey]` is missing/falsy, but `resp[singularKey]` is present
 *      as object or array                                 → normalize and return
 *   6) Anything else                                      → return []
 *
 * @param resp       The raw jXRL response object (usually from `client.jxrl.*`).
 * @param arrayKey   The plural field name, e.g. `"CustSrchRecArray"`.
 * @param singularKey Optional singular fallback, e.g. `"CustSrchRec"`. Defaults
 *                   to `arrayKey.replace(/Array$/, '')`.
 *
 * @example
 *   const rows = unwrapJxrlArray<CustSrchRec>(resp, 'CustSrchRecArray', 'CustSrchRec');
 *   rows.forEach(r => ...);  // always safe — never throws on object-vs-array
 */
export function unwrapJxrlArray<T>(
  resp: unknown,
  arrayKey: string,
  singularKey?: string,
): T[] {
  if (!isPlainObject(resp)) return [];

  const singular = singularKey ?? arrayKey.replace(/Array$/, '');
  const primary = resp[arrayKey];

  // 4) Null-wrapper on the primary slot → empty result.
  if (isNullWrapper(primary)) return [];

  // 1) Primary is already a true array.
  if (Array.isArray(primary)) return primary as T[];

  // 2) Primary is an object holding `{ [singular]: [...] | {...} }`.
  if (isPlainObject(primary)) {
    const inner = primary[singular];
    if (isNullWrapper(inner)) return [];
    if (Array.isArray(inner)) return inner as T[];
    if (isPlainObject(inner)) return [inner as T];
    // 3) Primary itself is the single record (no nested singular key).
    //    Only treat as record if it has more than just jXRL metadata.
    return [primary as T];
  }

  // 5) Primary missing — try the singular slot directly.
  const fallback = resp[singular];
  if (isNullWrapper(fallback)) return [];
  if (Array.isArray(fallback)) return fallback as T[];
  if (isPlainObject(fallback)) return [fallback as T];

  // 6) Nothing usable.
  return [];
}

/**
 * Extract the raw string value from a jXRL field that may be:
 *   - a plain string                          → returned as-is
 *   - a masked object `{"#text":"123...","@MaskVal":"XXX..."}` → returns `#text`
 *   - `null` / `undefined` / `{"#text": null}` → returns `""`
 *   - any other shape                         → returns `""`
 *
 * Mirrors the `txt()` helper at `JxChangeService.ts:206-210`.
 *
 * @example
 *   txt("123456789")                                         // "123456789"
 *   txt({ "#text": "123456789", "@MaskVal": "XXX-XX-6789" }) // "123456789"
 *   txt({ "#text": null })                                   // ""
 *   txt(null)                                                // ""
 */
export function txt(maybeMasked: unknown): string {
  if (typeof maybeMasked === 'string') return maybeMasked;
  if (isPlainObject(maybeMasked)) {
    const t = maybeMasked['#text'];
    if (typeof t === 'string') return t;
    if (typeof t === 'number' || typeof t === 'boolean') return String(t);
  }
  return '';
}

/**
 * Extract the `@MaskVal` display value from a masked jXRL object. Intended
 * only for customer-facing display surfaces (e.g. customer portal) where the
 * masked presentation is required. Staff surfaces should use {@link txt}.
 *
 * @example
 *   maskVal({ "#text": "123456789", "@MaskVal": "XXX-XX-6789" }) // "XXX-XX-6789"
 *   maskVal("123456789")                                          // ""
 *   maskVal(null)                                                 // ""
 */
export function maskVal(maybeMasked: unknown): string {
  if (isPlainObject(maybeMasked)) {
    const m = maybeMasked['@MaskVal'];
    if (typeof m === 'string') return m;
  }
  return '';
}

/**
 * Detect a jXRL error envelope. jXRL returns errors as either:
 *   - `resp.Details = [{ Code: '...', Description: '...' }, ...]`
 *   - `resp.RsStat = 'Fail'`
 *   - A non-empty MsgRecInfoArray with an ErrCode
 *
 * Mirrors the legacy `if (resp.Details) return []` pattern at
 * `CustSearchHelper.ts:59`.
 *
 * @example
 *   if (hasJxrlError(resp)) { showError(getJxrlErrorCode(resp)); return; }
 */
export function hasJxrlError(resp: unknown): boolean {
  if (!isPlainObject(resp)) return false;

  // `Details` is the canonical error envelope — if present and non-empty, fail.
  const details = resp['Details'];
  if (Array.isArray(details) && details.length > 0) return true;

  // `RsStat` = 'Fail' is the alt failure flag on some operations.
  const rsStat = resp['RsStat'];
  if (typeof rsStat === 'string' && rsStat.toLowerCase() === 'fail') return true;

  // MsgRecInfoArray[0].ErrCode populated also indicates error (common on Srch ops).
  const code = getJxrlErrorCode(resp);
  if (code) return true;

  return false;
}

/**
 * Extract a jXRL error code string from any of the common locations:
 *   - `resp.Details[0].Code`
 *   - `resp.SrchMsgRsHdr.MsgRecInfoArray[0].ErrCode`
 *   - `resp.MsgRsHdr.MsgRecInfoArray[0].ErrCode`
 *   - `resp.errorCode` (typed-endpoint shape from PCBJxChangeApi)
 *
 * Returns null if no code is found.
 */
export function getJxrlErrorCode(resp: unknown): string | null {
  if (!isPlainObject(resp)) return null;

  // 1) Details[0].Code — canonical jXRL error envelope.
  const details = resp['Details'];
  if (Array.isArray(details) && details.length > 0) {
    const first = details[0];
    if (isPlainObject(first)) {
      const code = first['Code'];
      if (typeof code === 'string' && code.length > 0) return code;
    }
  }

  // 2) Typed-endpoint shape: { success:false, errorCode:'1050', error:'...' }
  const errorCode = resp['errorCode'];
  if (typeof errorCode === 'string' && errorCode.length > 0) return errorCode;

  // 3) SrchMsgRsHdr / MsgRsHdr → MsgRecInfoArray[0].ErrCode.
  for (const hdrKey of ['SrchMsgRsHdr', 'MsgRsHdr'] as const) {
    const hdr = resp[hdrKey];
    if (!isPlainObject(hdr)) continue;
    const arr = hdr['MsgRecInfoArray'];
    const list = Array.isArray(arr) ? arr : isPlainObject(arr) ? [arr] : [];
    for (const item of list) {
      if (!isPlainObject(item)) continue;
      const inner = isPlainObject(item['MsgRecInfo']) ? item['MsgRecInfo'] : item;
      const code = (inner as Record<string, unknown>)['ErrCode'];
      if (typeof code === 'string' && code.length > 0) return code;
    }
  }

  return null;
}
